import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { folders, recipes } from '../lib/api';
import RecipeThumbnail from '../components/RecipeThumbnail';

function FolderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);

  const { data: folder, isLoading, error } = useQuery({
    queryKey: ['folder', id],
    queryFn: () => folders.get(id),
  });

  const { data: allRecipes } = useQuery({
    queryKey: ['recipes-all'],
    queryFn: () => recipes.list({ limit: 100 }),
    enabled: showAddModal,
  });

  const toggleFavoriteMutation = useMutation({
    mutationFn: (recipeId) => recipes.toggleFavorite(recipeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folder', id] });
    },
  });

  const removeFromFolderMutation = useMutation({
    mutationFn: (recipeId) => folders.removeRecipes(id, [recipeId]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folder', id] });
    },
  });

  const addToFolderMutation = useMutation({
    mutationFn: (recipeIds) => folders.addRecipes(id, recipeIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folder', id] });
      setShowAddModal(false);
    },
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !folder) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Folder not found</h2>
        <Link to="/folders" className="btn btn-primary">
          Back to Folders
        </Link>
      </div>
    );
  }

  // Get recipes not in this folder
  const availableRecipes = allRecipes?.recipes?.filter(
    (r) => !folder.recipes.some((fr) => fr.id === r.id)
  ) || [];

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      {/* Back button */}
      <button
        onClick={() => navigate('/folders')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back to Folders
      </button>

      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: folder.color || '#6b7280' }}
        >
          <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{folder.name}</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {folder.recipeCount} {folder.recipeCount === 1 ? 'recipe' : 'recipes'}
          </p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Recipes
        </button>
      </div>

      {/* Empty state */}
      {folder.recipes.length === 0 && (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">This folder is empty</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Add recipes to organize them here</p>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
            Add Recipes
          </button>
        </div>
      )}

      {/* Recipe grid */}
      {folder.recipes.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {folder.recipes.map((recipe) => (
            <div key={recipe.id} className="card overflow-hidden group">
              <Link to={`/recipe/${recipe.id}`}>
                <div className="aspect-video">
                  <RecipeThumbnail recipe={recipe} />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2">
                    {recipe.title}
                  </h3>
                  {recipe.sourceCreator && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {recipe.sourceCreator}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">{formatDate(recipe.createdAt)}</p>
                </div>
              </Link>

              {/* Actions */}
              <div className="px-4 pb-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => toggleFavoriteMutation.mutate(recipe.id)}
                  className={`btn-icon ${recipe.isFavorite ? 'text-yellow-500' : 'text-gray-400'}`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill={recipe.isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </button>
                <button
                  onClick={() => removeFromFolderMutation.mutate(recipe.id)}
                  className="btn btn-ghost text-sm flex-1"
                >
                  Remove from folder
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add recipes modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card max-w-2xl w-full max-h-[80vh] flex flex-col animate-fadeIn">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Add Recipes to Folder</h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {availableRecipes.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  All your recipes are already in this folder
                </p>
              ) : (
                <div className="space-y-2">
                  {availableRecipes.map((recipe) => (
                    <label
                      key={recipe.id}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                        onChange={(e) => {
                          if (e.target.checked) {
                            addToFolderMutation.mutate([recipe.id]);
                          }
                        }}
                      />
                      <div className="w-12 h-12 rounded flex-shrink-0 overflow-hidden">
                        <RecipeThumbnail recipe={recipe} size="small" showTitle={false} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">{recipe.title}</p>
                        {recipe.sourceCreator && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">{recipe.sourceCreator}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button onClick={() => setShowAddModal(false)} className="btn btn-secondary">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FolderDetail;
